import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { PagedItems } from '@texas/types';
import { httpClient } from '../httpClient/httpClient';
import { baseApiUrl } from '../httpClient/constants';
import { objectToServerQueryString } from '@texas/utils/helpers/commonHelpers';
import { SimpleContact } from './contactsApi';
import { Sample } from './samplesApi';
import { CompositionGroupSample } from './compositionGroup/compositionGroupApi';
import { AutoUpdateRequest, AutoUpdateResponse } from './autoUpdateTypes';

export enum ProductApprovalState {
  Pending = 1,
  Accepted = 2,
  Rejected = 3,
  Cancelled = 4,
  OnHold = 5,
}

export enum ProductApprovalRejectReason {
  Price = 1,
  Quality = 2,
  SampleLeadTime = 3,
  ProductionLeadTime = 4,
  AnswerTime = 5,
  Other = 6,
  DesignLayout = 7,
}

export interface ProductApproval {
  id: number;
  number: string;
  formatNumber: string;
  articleItemCode: string;
  articleFolderId: number;
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  supplierId: number;
  supplierName: string;
  variantName: string;
  variantId: number;
  state: ProductApprovalState;
  created: string;
  supplierContactEmail: string;
  supplierContactName: string;
  supplierContactId: number;
  customerContactEmail: string;
  customerContactName: string;
  customerContactId: number;
  issuedBy: string;
  matrixNodeXId: number | null;
  matrixNodeXValue: string;
  matrixNodeXName: string;
  matrixNodeYId: number | null;
  matrixNodeYValue: string;
  matrixNodeYName: string;
  fileId: number | null;
  fileIdentifier: string | null;
  compositionGroupId: number;
  compositionGroupVersion: number;
  handledDate: string | null;
  handledBy: string | null;
  compositionGroupBasedOnOption: number;
  compositionGroupBasedOnOptionNote: string | null;
  linkedVariantId: number | null;
  handleReason: ProductApprovalRejectReason[] | null;
  handledNote: string | null;
  mainSupplier: { name: string; branch: string } | null;
  followers: { name: string; branch: string }[] | null;
  sample: Sample | null;
  externalSupplierItemCode: string;
}

export interface SimpleProductApproval {
  productApprovalId: number;
  number: string;
  formatNumber: string;
  state: ProductApprovalState;
  isMain: boolean;
  fileId: number | null;
  fileIdentifier: string | null;
  mainSample: CompositionGroupSample | null;
  contact: SimpleContact;
  handledDate: string | null;
  handledBy: string | null;
  handleReason: ProductApprovalRejectReason[] | null;
  externalSupplierItemCode: string;
}

export interface PaFilterOptions {
  sampleLifecycles?: SampleLifecycle[];
  searchTerm?: string;
  state?: ProductApprovalState;
  branchIds?: number[];
  supplierIds?: number[];
  productApprovalIds?: number[];
}

export interface FilterOptionIdsQuery {
  sampleState?: SampleLifecycle;
  searchTerm?: string;
  state?: ProductApprovalState;
  branchId?: number;
  supplierId?: number;
}

export interface ProductApprovalsSearchQuery extends PaFilterOptions {
  articleId?: number;
  customerId?: number;
  searchTerm: string;
  sortBy: string;
  sortDesc: boolean;
  page: number;
  pageSize: number;
}

interface FilterPaQuery {
  state?: ProductApprovalState;
  branchId?: number;
  matrixNodeXId?: number | null;
  matrixNodeYId?: number | null;
}

export interface CreateProductApprovalData {
  variantId: number;
  supplierId: number;
  branchId: number;
  matrixNodeXId: number | null;
  matrixNodeYId: number | null;
  compositionGroupId: number;
  customerContactId: number;
  supplierContactId: number;
  externalSupplierItemCode: string;
  sampleReadyDate: Date | null;
  supplierNote: string;
}

export interface CreateProductApprovalsRequest {
  data: CreateProductApprovalData[];
}

export interface CountProductApprovalsQuery {
  state: ProductApprovalState;
  variantId: number;
  branchId: number;
  supplierId: number;
  matrixNodeXId?: number;
  matrixNodeYId?: number;
  onlyArchived?: boolean;
}

interface Count {
  count: number;
}

export interface ProductApprovalHistory {
  id: number;
  user: string;
  approvalState: ProductApprovalState;
  date: string;
  rejectReasons: ProductApprovalRejectReason[] | null;
  note: string;
}

export interface RejectPaRequest extends ChangeStateRequest {
  reasons: ProductApprovalRejectReason[];
}

export interface AcceptPaRequest extends ChangeStateRequest {
  rejectPendingPas: {
    reasons: ProductApprovalRejectReason[];
    note: string;
    id: number;
  }[];
}

export interface ChangeStateRequest {
  note: string;
  assignedUserIds: number[];
}

export interface GeneratedProductApproval {
  customerId: number;
  customerName: string;
  variantId: number;
  articleId: number;
  supplierId: number;
  supplierName: string;
  branchId: number;
  branchName: string;
  compositionGroupId: number;
  customerContact: SimpleContact | null;
  supplierContact: SimpleContact | null;
  articleFolderId: number;
  matrixNodeXValue: string | null;
  matrixNodeYValue: string | null;
  matrixNodeXName: string | null;
  matrixNodeYName: string | null;
  matrixNodeXId: number | null;
  matrixNodeYId: number | null;
  version: number;
  option: number;
  articleItemCode: string;
  articleName: string;
  expectedReadyDate: string | null;
}

export interface UpdateSupplierItemCodeRequest {
  supplierItemCode: string;
}

export const productApprovalsApi = {
  getCustomerFilters: (customerId: number) =>
    httpClient.get<{
      branches: { id: number; name: string }[];
      suppliers: { id: number; name: string }[];
    }>(`customers/${customerId}/product-approval-filters`),
  get: (id: number) => httpClient.get<ProductApproval>(`productapproval/${id}`),
  getForVariant: (variantId: number, params?: FilterPaQuery) =>
    httpClient.get<ProductApproval[]>(`productapproval/variant/${variantId}`, {
      params,
    }),
  getAll: (params: ProductApprovalsSearchQuery) =>
    httpClient.get<PagedItems<ProductApproval>>(`productapproval/all`, {
      params,
    }),
  getCount: (params: CountProductApprovalsQuery) =>
    httpClient.get<Count>(`productapproval/count`, {
      params,
    }),
  create: (request: CreateProductApprovalsRequest) =>
    httpClient.post<ProductApproval[]>(`productapproval`, { data: request }),
  reject: (id: number, request: RejectPaRequest) =>
    httpClient.put(`productapproval/${id}/reject`, { data: request }),
  accept: (id: number, request: AcceptPaRequest) =>
    httpClient.put(`productapproval/${id}/accept`, { data: request }),
  setAsPending: (id: number, request: ChangeStateRequest) =>
    httpClient.put(`productapproval/${id}/pending`, { data: request }),
  cancel: (id: number, request: ChangeStateRequest) =>
    httpClient.put(`productapproval/${id}/cancel`, { data: request }),
  getHistory: (id: number) =>
    httpClient.get<ProductApprovalHistory[]>(`productapproval/${id}/history`),

  downloadMultipleSupplierProductApprovals: (ids: number[]) =>
    `${baseApiUrl}/productapproval/download/multiple/supplier${objectToServerQueryString(
      { ids: ids },
    )}`,

  downloadMultipleCustomerProductApprovals: (
    hideMaterials: boolean,
    ids: number[],
  ) =>
    `${baseApiUrl}/productapproval/download/multiple/customer/${hideMaterials}${objectToServerQueryString(
      { ids: ids },
    )}`,

  downloadSupplierProductApproval: (id: number) =>
    `${baseApiUrl}/productapproval/${id}/download/supplier`,

  downloadCustomerProductApproval: (id: number, hideMaterials: boolean) =>
    `${baseApiUrl}/productapproval/${id}/download/customer/${hideMaterials}`,
  generate: (compositionGroupIds: number[]) =>
    httpClient.get<GeneratedProductApproval[]>(`productapproval/generate`, {
      params: { compositionGroupIds: compositionGroupIds },
    }),
  updatePhoto: (id: number, fileId: number) =>
    httpClient.put(`/productapproval/${id}/photo/${fileId}`),
  autoUpdateExternalSupplierItemCode: (
    request: AutoUpdateRequest<UpdateSupplierItemCodeRequest>,
    id: number,
  ) =>
    httpClient.put<AutoUpdateResponse<UpdateSupplierItemCodeRequest>>(
      `/productapproval/auto/${id}/supplier-item-code`,
      {
        data: request,
      },
    ),
  deletePhoto: (id: number) =>
    httpClient.delete(`/productapproval/${id}/photo`),
  downloadExcel: (data: { productApprovalIds: number[] }) =>
    httpClient.post<any>(`excel/product-approval/export`, {
      data,
      responseType: 'blob',
    }),
};
